import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import PageMeta from "@/components/PageMeta";
import { ArrowRight, Maximize2, Home as HomeIcon, BedDouble } from "lucide-react";

// Royalhouse house catalog — prefab (no Ziegel). All images generated for Royalhouse.
const HOUSES = [
  {
    id: "wien-design", name: "Royalhouse Wien 128", city: "Wien",
    type: "einfamilienhaus", roof: "flach", area: 128, bedrooms: 4,
    cover: "/images/haus-wien-design.jpg",
    short: "Modernes Stadthaus mit Flachdach, klarer Volumetrie und großzügigen Glasflächen für urbane Lagen.",
  },
  {
    id: "salzburg-walm", name: "Royalhouse Salzburg 142", city: "Salzburg",
    type: "einfamilienhaus", roof: "walm", area: 142, bedrooms: 4,
    cover: "/images/haus-salzburg-walm.jpg",
    short: "Walmdach-Haus mit ruhiger Fassadensprache — passend zur alpinen Region, in moderner Interpretation.",
  },
  {
    id: "linz-flach", name: "Royalhouse Linz 115", city: "Linz",
    type: "einfamilienhaus", roof: "flach", area: 115, bedrooms: 3,
    cover: "/images/haus-linz-flach.jpg",
    short: "Kompaktes Familienhaus mit Flachdach. Effiziente Grundrisse, bodentiefe Verglasung, klare Linien.",
  },
  {
    id: "graz-design", name: "Royalhouse Graz 153 Design", city: "Graz",
    type: "einfamilienhaus", roof: "flach", area: 153, bedrooms: 5,
    cover: "/images/haus-graz-design.jpg",
    short: "Design-Haus mit kontrastierender Materialwahl — anthrazitfarbenes Erdgeschoss, weißes Obergeschoss.",
  },
  {
    id: "innsbruck-walm", name: "Royalhouse Innsbruck 130", city: "Innsbruck",
    type: "einfamilienhaus", roof: "walm", area: 130, bedrooms: 4,
    cover: "/images/haus-innsbruck-walm.jpg",
    short: "Alpine Architektur in zeitgemäßer Form. Tiroler Charakter, Holz-Akzente, ruhige Proportionen.",
  },
  {
    id: "stpoelten-flach", name: "Royalhouse St. Pölten 144", city: "St. Pölten",
    type: "einfamilienhaus", roof: "flach", area: 144, bedrooms: 4,
    cover: "/images/haus-stpoelten-flach.jpg",
    short: "Zweigeschossiges Familienhaus mit Flachdach und großzügigem Garten — ideal für junge Familien.",
  },
  {
    id: "bregenz-pultdach", name: "Royalhouse Bregenz 138", city: "Bregenz",
    type: "einfamilienhaus", roof: "pult", area: 138, bedrooms: 4,
    cover: "/images/haus-bregenz-pultdach.jpg",
    short: "Pultdach-Architektur mit hoher Glasfront — optimiert für solare Gewinne und Bodenseeklima.",
  },
  {
    id: "doppelhaus-twin", name: "Royalhouse Twin 95", city: "Mehrere Standorte",
    type: "doppelhaus", roof: "flach", area: 95, bedrooms: 3,
    cover: "/images/haus-doppelhaus-twin.jpg",
    short: "Doppelhaus für effiziente Grundstücksnutzung. Zwei spiegelgleiche Einheiten mit privaten Außenbereichen.",
  },
  {
    id: "bungalow-flach", name: "Royalhouse Bungalow 110", city: "Mehrere Standorte",
    type: "bungalow", roof: "flach", area: 110, bedrooms: 3,
    cover: "/images/haus-bungalow-flach.jpg",
    short: "Eingeschossiger Bungalow mit Flachdach. Barrierearm, lebenslang nutzbar, kompakter Grundriss.",
  },
];

const COPY = {
  de: {
    eyebrow: "Häuser · Katalog",
    h1a: "Royalhouse",
    h1b_em: "Häuser.",
    lede: "Massiv und energieeffizient in Royalhouse-Vorfertigung — werkseitig produziert, präzise montiert, mit Fixtermin und festem Funktionsumfang. Verfügbar in Österreich und Deutschland.",
    usp_eyebrow: "Warum Royalhouse Prefab",
    usps: [
      { num: "01", title: "Industrielle Vorfertigung — kein Ziegel", text: "Komplette Module entstehen im Werk: Wände, Decken, Fenster, Haustechnik. Kürzere Bauzeit, kontrollierte Qualität, keine Witterungsschäden im Bau." },
      { num: "02", title: "Fixtermin & klarer Funktionsumfang", text: "Schriftlich definierter Liefer- und Montagetermin. Klare Ausstattung, dokumentierte Materialqualität, keine Überraschungen während der Bauzeit." },
      { num: "03", title: "Energieeffizient ab Werk", text: "Hochwertige Dämmung, vorbereitete PV, Wärmepumpe und KI-Energiemanagement durch ECO Building Technik. Niedrige Betriebskosten von Tag eins." },
      { num: "04", title: "Architektonisch klar, kein Katalog-Look", text: "Reduzierte Formen, ehrliche Materialien, keine billigen Effekte. Häuser, die in 20 Jahren noch zeitlos wirken." },
    ],
    types: {
      einfamilienhaus: "Einfamilienhäuser",
      doppelhaus: "Doppelhäuser",
      bungalow: "Bungalow",
      reihenhaus: "Reihenhäuser",
    },
    filter_all: "Alle Modelle",
    filter_type: "Haustyp",
    badge_prefab: "Royalhouse Prefab",
    card_area: "Wohnfläche",
    card_bedrooms: "Schlafräume",
    card_cta: "Modell ansehen",
    cta_title: "Modell gefunden?",
    cta_body: "Wir prüfen Ihr Grundstück, planen die Bebauung und nennen Ihnen einen Festpreis innerhalb von zwei Wochen.",
    cta_btn: "Beratung anfragen",
    note: "Hinweis: Modelle sind Beispieldarstellungen. Wohnfläche, Grundriss und Ausstattung werden projektbezogen angepasst. Preise auf Anfrage.",
  },
};

export default function HaeuserPage() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.de;
  const [typeFilter, setTypeFilter] = useState("alle");

  const filtered = useMemo(() => {
    if (typeFilter === "alle") return HOUSES;
    return HOUSES.filter((h) => h.type === typeFilter);
  }, [typeFilter]);

  const typeButtons = [
    { id: "alle", label: c.filter_all },
    { id: "einfamilienhaus", label: c.types.einfamilienhaus },
    { id: "doppelhaus", label: c.types.doppelhaus },
    { id: "bungalow", label: c.types.bungalow },
  ];

  return (
    <div data-testid="haeuser-page">
      <PageMeta pageKey="haeuser" />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex items-center gap-4 mb-10">
          <span className="w-12 h-px bg-white/55" />
          <span className="text-[11px] tracking-[0.32em] uppercase text-white/65">{c.eyebrow}</span>
        </div>
        <h1 className="font-display font-light text-5xl sm:text-6xl md:text-7xl lg:text-8xl uppercase tracking-tighter leading-[0.95]">
          {c.h1a}
          <br />
          <span className="italic font-extralight">{c.h1b_em}</span>
        </h1>
        <p className="mt-10 max-w-2xl text-base md:text-lg text-white/80 leading-[1.65] font-light">{c.lede}</p>
      </section>

      {/* USPs */}
      <section className="py-20 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 border-t border-white/10">
        <p className="text-[11px] tracking-[0.3em] uppercase text-white/55 mb-3">{c.usp_eyebrow}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mt-12">
          {c.usps.map((u) => (
            <div key={u.num} className="bg-[#051630] p-8 md:p-10">
              <span className="text-[10px] tracking-[0.3em] uppercase text-white/40">{u.num}</span>
              <h3 className="font-display text-lg uppercase tracking-tight mt-4 mb-3">{u.title}</h3>
              <p className="text-sm text-white/70 leading-[1.7] font-light">{u.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Filter */}
      <section className="py-12 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 border-t border-white/10">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-[10px] tracking-[0.3em] uppercase text-white/55 mr-2">{c.filter_type}:</span>
          {typeButtons.map((tb) => (
            <button
              key={tb.id}
              onClick={() => setTypeFilter(tb.id)}
              data-testid={`haus-filter-${tb.id}`}
              className={`text-[11px] tracking-[0.22em] uppercase font-medium px-5 py-3 border transition-colors ${
                typeFilter === tb.id
                  ? "bg-white text-[#051630] border-white"
                  : "border-white/25 text-white/70 hover:border-white hover:text-white"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>
      </section>

      {/* House grid */}
      <section className="pb-24 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {filtered.map((h) => (
            <article key={h.id} className="bg-[#051630] group" data-testid={`haus-card-${h.id}`}>
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={h.cover}
                  alt={`${h.name} — Royalhouse Prefab`}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
                  loading="lazy"
                />
                <div
                  className="absolute inset-0"
                  style={{ background: "linear-gradient(to top, rgba(5,22,48,0.5), rgba(5,22,48,0.05) 60%)" }}
                />
                <span className="absolute top-4 left-4 text-[9px] tracking-[0.28em] uppercase bg-white/10 backdrop-blur-md text-white px-3 py-1.5 border border-white/25">
                  {c.badge_prefab}
                </span>
              </div>
              <div className="p-7 md:p-8">
                <p className="text-[10px] tracking-[0.3em] uppercase text-white/45 mb-3">{h.city}</p>
                <h3 className="font-display text-xl md:text-2xl uppercase tracking-tight mb-4">{h.name}</h3>
                <p className="text-sm text-white/70 leading-[1.65] font-light mb-6 line-clamp-3">{h.short}</p>
                <div className="grid grid-cols-3 gap-4 mb-6 border-t border-white/10 pt-5">
                  <div>
                    <Maximize2 size={14} strokeWidth={1.2} className="text-white/50 mb-2" />
                    <p className="text-[9px] tracking-[0.25em] uppercase text-white/45">{c.card_area}</p>
                    <p className="text-base font-medium text-white mt-1">{h.area} m²</p>
                  </div>
                  <div>
                    <BedDouble size={14} strokeWidth={1.2} className="text-white/50 mb-2" />
                    <p className="text-[9px] tracking-[0.25em] uppercase text-white/45">{c.card_bedrooms}</p>
                    <p className="text-base font-medium text-white mt-1">{h.bedrooms}</p>
                  </div>
                  <div>
                    <HomeIcon size={14} strokeWidth={1.2} className="text-white/50 mb-2" />
                    <p className="text-[9px] tracking-[0.25em] uppercase text-white/45">{c.filter_type}</p>
                    <p className="text-base font-medium text-white mt-1 capitalize">{c.types[h.type]}</p>
                  </div>
                </div>
                <Link
                  to="/kontakt"
                  data-testid={`haus-cta-${h.id}`}
                  className="group/btn inline-flex items-center gap-4 border-b border-white/40 hover:border-white pb-1.5 text-[10px] tracking-[0.28em] uppercase text-white/80 hover:text-white transition-colors"
                >
                  <span>{c.card_cta}</span>
                  <ArrowRight size={13} strokeWidth={1.5} className="transition-transform group-hover/btn:translate-x-1" />
                </Link>
              </div>
            </article>
          ))}
        </div>
        <p className="text-xs text-white/45 mt-10 leading-relaxed max-w-3xl">{c.note}</p>
      </section>

      {/* CTA */}
      <section className="py-24 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 border-t border-white/10 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7 min-w-0">
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] break-words hyphens-auto">
            {c.cta_title}
          </h2>
          <p className="mt-6 max-w-xl text-white/75 leading-[1.7] font-light">{c.cta_body}</p>
        </div>
        <div className="md:col-span-5 flex md:justify-end md:items-end">
          <Link
            to="/kontakt"
            data-testid="haeuser-cta"
            className="group inline-flex items-center gap-6 bg-white text-[#051630] font-semibold tracking-[0.2em] uppercase text-[11px] px-8 py-5 border border-white hover:bg-transparent hover:text-white transition-colors"
          >
            <span>{c.cta_btn}</span>
            <ArrowRight size={16} strokeWidth={2} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
